import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { DISCUSSION_EMOJI, EMOJI_GROUPS, PACK_NAME, emojiUrl } from "../config/discussionEmoji";
import EmojiService from "../services/emoji.service";
import { useAuthStore } from "../store/authStore";

/**
 * Merges the built-in Vitrine emoticon library with the current institution's
 * uploaded custom emoji into one catalog the emoji components consume. Both
 * kinds are images and carry a `url`, so renderers need no special-casing.
 * Falls back to the built-in library when no provider is mounted, so components
 * always render something.
 */
const builtInList = DISCUSSION_EMOJI.map((e) => ({
  code: e.code,
  name: e.name,
  group: e.group,
  keywords: e.keywords,
  url: emojiUrl(e.code),
}));

// Built-ins grouped once at module load — the shape never changes.
const builtInGroups = EMOJI_GROUPS.map((g) => ({
  key: g.key,
  name: g.name,
  items: builtInList.filter((e) => e.group === g.key),
}));

const buildCatalog = (customList = []) => {
  const list = [...builtInList, ...customList];
  return {
    list,
    byCode: Object.fromEntries(list.map((e) => [e.code, e])),
    knownCodes: new Set(list.map((e) => e.code)),
    // An institution's own emoji lead the picker — they're the few a user
    // recognises instantly, against 126 built-ins they have to hunt through.
    groups: customList.length
      ? [{ key: "custom", name: "Your institution", items: customList }, ...builtInGroups]
      : builtInGroups,
    packName: PACK_NAME,
  };
};

const BUILTIN = buildCatalog([]);
const EmojiCatalogContext = createContext(BUILTIN);

export const EmojiCatalogProvider = ({ children }) => {
  // ONLY fetch when someone is actually signed in. /discussion-emoji is
  // auth-gated, so an anonymous visitor gets a 401 — and apiClient's 401
  // interceptor responds by attempting a token refresh and then hard-
  // redirecting to /login, which remounts this provider and starts again.
  // On a tenant login portal that is an infinite reload loop: the page
  // flickers between skeletons and blank and never settles. A logged-out
  // visitor has no institution-custom emoji to show anyway.
  const isAuthenticated = useAuthStore((s) => !!s.user);

  const { data: custom = [] } = useQuery({
    queryKey: ["custom-emoji"],
    queryFn: async () => (await EmojiService.listCustom())?.data?.emojis || [],
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  });

  const value = useMemo(
    () =>
      buildCatalog(
        custom.map((e) => ({ code: e.code, name: e.name, url: e.url, group: "custom", keywords: [] }))
      ),
    [custom]
  );

  return <EmojiCatalogContext.Provider value={value}>{children}</EmojiCatalogContext.Provider>;
};

export const useEmojiCatalog = () => useContext(EmojiCatalogContext);
